import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  ButtonGroup,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { axiosConfig } from "../../axios/axiosConfig";
import { method } from "lodash";
import toast from "react-hot-toast";

// ─── Dummy Data ────────────────────────────────────────────────────────────────

const PLAN_COLORS = {
  Basic: "#F59E0B",
  Premium: "#016551",
  Pro: "#04343a",
};

const PIE_COLORS = ["#016551", "#F59E0B", "#04343a", "#DC2626", "#0EA5E9"];

// ─── Helper Components ─────────────────────────────────────────────────────────

const StatCard = ({ icon, title, value, subtitle, color, progress }) => (
  <Card
    sx={{
      background: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
      height: "100%",
      overflow: "hidden",
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.2,
              mt: 0.5,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            background: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      {progress !== undefined && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                bgcolor: color.replace("20", ""),
                borderRadius: 3,
              },
            }}
          />
          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
            {progress}% of total users
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box
    sx={{
      background: "#04343a",
      borderRadius: "10px 10px 0 0",
      px: 2.5,
      py: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    {icon}
    <Box>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const ExpiredUserDashboard = () => {
  const [filterPeriod, setFilterPeriod] = useState("7days");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // ── Extend Dialog ──
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendSubmitting, setExtendSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [extendForm, setExtendForm] = useState({
    timedelta: "",
    target_plan_id: "",
    new_plan_id: "",
    days_extend: 1,
    conversion: "plan_extension",
    tokens: 100,
    remark: "",
  });
  console.log(extendForm, "extendForm");
  const filterPeriods = [
    { label: "Today", value: "today" },
    { label: "Last 7 Days", value: "7days" },
    { label: "Last 30 Days", value: "30days" },
    { label: "All", value: "all" },
  ];

  const fetchExpiredUsers = async () => {
    try {
      setLoading(true);
      setExpiredUsers([]);
      const params = new URLSearchParams();
      if (filterPeriod === "7days") params.set("timedelta", "7");
      else if (filterPeriod === "30days") params.set("timedelta", "30");
      if (selectedPlanId) params.set("plan_id", selectedPlanId);
      const query = params.toString();
      const url = `/subscription/check-upcoming-expiry${query ? `?${query}` : ""}`;
      const response = await axiosConfig({ method: "get", url });
      setUserCount(response?.data?.count || 0);
      setExpiredUsers(response?.data?.data || []);
    } catch (error) {
      console.error("fetchExpiredUsers error:", error);
      setExpiredUsers([]);
      setUserCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpiredUsers();
  }, [filterPeriod, selectedPlanId]);

  useEffect(() => {
    axiosConfig({ method: "get", url: "/subscription/subscription_plan" })
      .then((res) => {
        const allPlans =
          res?.data?.response?.plans?.flatMap((p) => p.data) || [];
        setPlans(allPlans);
      })
      .catch(() => setPlans([]));
  }, []);

  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  const [bulkReminderPlanId, setBulkReminderPlanId] = useState("");
  const [bulkReminderSubmitting, setBulkReminderSubmitting] = useState(false);

  const handleBulkReminder = async () => {
    if (!bulkReminderPlanId) {
      toast.error("Please select a plan");
      return;
    }
    try {
      setBulkReminderSubmitting(true);
      const res = await axiosConfig({
        method: "post",
        url: "/subscription/plan-expiry-reminder-batch",
        data: {
          timedelta: filterPeriodToTimedelta[filterPeriod] ?? 0,
          plan_id: Number(bulkReminderPlanId),
        },
      });
      toast.success(res?.data?.response || "Bulk reminders sent successfully!");
      setBulkReminderOpen(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.response || "Failed to send reminders",
      );
    } finally {
      setBulkReminderSubmitting(false);
    }
  };

  const filterPeriodToTimedelta = {
    today: 0,
    "7days": 7,
    "30days": 30,
    all: 0,
  };

  const handleRemind = async (user) => {
    try {
      const payload = {
        userid: user.userid,
        plan_name: user.plan_name,
        expiry_date: user.expiry_date,
        user_name: user.name || user.full_name || user.user_name,
      };
      const res = await axiosConfig({
        method: "post",
        url: "/subscription/plan-expiry-reminder-single",
        data: payload,
      });
      toast.success(res?.data?.response || "Notification sent successfully!");
    } catch (error) {
      toast.error(error?.response?.data?.response || "Failed to send reminder");
    }
  };

  const handleExtendSubmit = async () => {
    const daysToExtend = Number(extendForm.days_extend);
    if (!daysToExtend || daysToExtend < 1) {
      toast.error("Days to Extend is mandatory");
      return;
    }
    try {
      setExtendSubmitting(true);
      const payload = {
        timedelta: filterPeriodToTimedelta[filterPeriod] ?? 0,
        target_plan_id: extendForm.target_plan_id,
        days_extend: daysToExtend,
        conversion: extendForm.conversion,
        tokens: Number(extendForm.tokens),
        remark: extendForm.remark,
      };
      if (extendForm.conversion === "plan_change") {
        payload.new_plan_id = extendForm.new_plan_id;
      }
      await axiosConfig({
        method: "post",
        url: "/subscription/extend-upcoming-expiry",
        data: payload,
      });
      setExtendOpen(false);
      fetchExpiredUsers();
    } catch (error) {
      console.error("extend-upcoming-expiry error:", error);
    } finally {
      setExtendSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* ── Page Header ── */}

      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PersonRemoveIcon sx={{ color: "#fff", fontSize: 34 }} />
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 22,
                lineHeight: 1.2,
              }}
            >
              Expired User Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
              Monitor expired users, renewals, and subscription health
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`As of ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
          sx={{
            background: "rgba(255,255,255,0.15)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 12,
          }}
          icon={
            <CalendarTodayIcon
              sx={{ color: "#fff !important", fontSize: 14 }}
            />
          }
        />
      </Box>
      {/* ── Expired Users Table ── */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <SectionHeader
          icon={<PersonRemoveIcon sx={{ color: "#fff", fontSize: 20 }} />}
          title="Expired Users — Detailed View"
          subtitle={`Showing ${userCount} records`}
        />

        {/* Table filters */}

        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            borderBottom: "1px solid #f1f5f9",
            justifyContent: "space-between",
          }}
        >
          {/* Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name, ID, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: 13,
                },
              }}
            />

            {/* Period filter */}
            <ButtonGroup size="small" variant="outlined">
              {filterPeriods.map((p) => (
                <Button
                  key={p.value}
                  onClick={() => setFilterPeriod(p.value)}
                  sx={{
                    fontSize: 12,
                    borderColor: "#e2e8f0",
                    color: filterPeriod === p.value ? "#fff" : "#64748b",
                    background:
                      filterPeriod === p.value ? "#04343a" : "transparent",
                    "&:hover": {
                      background:
                        filterPeriod === p.value ? "#04343a" : "#f8fafc",
                      borderColor: "#e2e8f0",
                    },
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </ButtonGroup>

          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Plan filter dropdown */}
            <FormControl size="small" sx={{ width: 260 }}>
              <InputLabel sx={{ fontSize: 13 }}>Filter by Plan</InputLabel>
              <Select
                label="Filter by Plan"
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                sx={{ fontSize: 13, borderRadius: "8px" }}
              >
                <MenuItem value="">
                  <em>All Plans</em>
                </MenuItem>
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.plan_name || plan.name}
                    {plan.plan_code ? ` (${plan.plan_code})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Bulk Extend Button */}
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setExtendForm((p) => ({
                  ...p,
                  target_plan_id: "",
                  new_plan_id: "",
                  remark: "",
                }));
                setExtendOpen(true);
              }}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                background: "#016551",
                color: "#fff",
                borderRadius: "8px",
                px: 2,
                boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                },
              }}
            >
              Bulk Extend (
              {filterPeriods.find((p) => p.value === filterPeriod)?.label})
            </Button>

            {/* Bulk Reminder Button */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setBulkReminderPlanId("");
                setBulkReminderOpen(true);
              }}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                borderColor: "#04343a",
                color: "#04343a",
                borderRadius: "8px",
                px: 2,
                "&:hover": { background: "#f0f4f4", borderColor: "#04343a" },
              }}
            >
              Bulk Reminder (
              {filterPeriods.find((p) => p.value === filterPeriod)?.label})
            </Button>
          </Box>

          {/* Status filter */}
          {/* <ButtonGroup size="small" variant="outlined">
            {["all", "active", "inactive"].map((s) => (
              <Button
                key={s}
                onClick={() => setActiveStatusFilter(s)}
                sx={{
                  fontSize: 12,
                  textTransform: "capitalize",
                  borderColor: "#e2e8f0",
                  color: activeStatusFilter === s ? "#fff" : "#64748b",
                  background:
                    activeStatusFilter === s
                      ? s === "active"
                        ? "#016551"
                        : s === "inactive"
                          ? "#DC2626"
                          : "#04343a"
                      : "transparent",
                  "&:hover": {
                    background: "transparent",
                    borderColor: "#e2e8f0",
                  },
                }}
              >
                {s === "all"
                  ? "All"
                  : s === "active"
                    ? `Active (${activeCount})`
                    : `Inactive (${inactiveCount})`}
              </Button>
            ))}
          </ButtonGroup> */}
        </Box>

        {/* Table */}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f8fafc" }}>
                {[
                  "User ID / Name",
                  "Phone / Email",
                  "Last Login",
                  "Last Renewal",
                  "Plan Type",
                  "Joining Date",
                  "Expiry Date",
                  "Status",
                  "Days Expired",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#475569",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid #e2e8f0",
                      py: 1.2,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <LinearProgress
                      sx={{
                        maxWidth: 300,
                        mx: "auto",
                        borderRadius: 2,
                        "& .MuiLinearProgress-bar": { bgcolor: "#016551" },
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 1 }}>
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : expiredUsers?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{ py: 4, color: "#94a3b8", fontSize: 14 }}
                  >
                    No expired users found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                expiredUsers?.map((user, idx) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      "&:hover": { background: "#f8fafc" },
                      background: idx % 2 === 0 ? "#fff" : "#fafcff",
                    }}
                  >
                    {/* User ID / Name */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 13,
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg,#04343a,#016551)",
                          }}
                        >
                          {(user?.username || user?.user_name)?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#0f172a",
                            }}
                          >
                            {user.username || user.user_name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                            {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Phone / Email */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "#334155" }}>
                        {user.phoneno}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {user.email}
                      </Typography>
                    </TableCell>

                    {/* Last Login */}
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: "#475569",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "-"}
                    </TableCell>

                    {/* Last Renewal */}
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: "#475569",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.last_renewal || user.lastRenewal
                        ? new Date(
                            user.last_renewal || user.lastRenewal,
                          ).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </TableCell>

                    {/* Plan Type */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.3,
                        }}
                      >
                        <Chip
                          label={user.plan_name}
                          size="small"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              user.plan_name === "Pro"
                                ? "#04343a"
                                : user.plan_name === "Premium"
                                  ? "#016551"
                                  : "#F59E0B",
                            color: "#fff",
                            height: 22,
                          }}
                        />
                        {user.plan_code && (
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: "#94a3b8",
                              fontWeight: 500,
                              pl: 0.5,
                            }}
                          >
                            {user.plan_code}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Joining Date */}
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: "#475569",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.date_joined
                        ? new Date(user.date_joined).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "-"}
                    </TableCell>

                    {/* Expiry Date */}
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: "#DC2626",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user?.expiry_date
                        ? new Date(user.expiry_date).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "-"}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={
                          user.active ? "Active" : user.user_type || "In-Active"
                        }
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          background: user.active ? "#edfaf3" : "#fef2f2",
                          color: user.active ? "#016551" : "#DC2626",
                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Days Expired */}
                    <TableCell>
                      <Chip
                        label={user?.expired_ago || "-"}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,

                          background:
                            user?.expired_ago === "Expires today"
                              ? "#FEF3C7"
                              : user?.expired_ago
                                    ?.toLowerCase()
                                    ?.includes("day")
                                ? "#FED7AA"
                                : "#FEE2E2",

                          color:
                            user?.expired_ago === "Expires today"
                              ? "#92400E"
                              : user?.expired_ago
                                    ?.toLowerCase()
                                    ?.includes("day")
                                ? "#9A3412"
                                : "#991B1B",

                          height: 22,
                        }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Send Renewal Reminder">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRemind(user)}
                            sx={{
                              fontSize: 10,
                              py: 0.3,
                              px: 1,
                              minWidth: "unset",
                              borderColor: "#016551",
                              color: "#016551",
                              "&:hover": {
                                background: "#edfaf3",
                                borderColor: "#016551",
                              },
                              borderRadius: "6px",
                              textTransform: "none",
                            }}
                          >
                            Remind
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table footer */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            Showing {filteredUsers.length} of {expiredUsers.length} expired
            users
          </Typography> */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* <Chip
              label={`Active: ${activeCount}`}
              size="small"
              sx={{
                background: "#edfaf3",
                color: "#016551",
                fontSize: 11,
                fontWeight: 600,
              }}
            /> */}
            {/* <Chip
              label={`Inactive: ${inactiveCount}`}
              size="small"
              sx={{
                background: "#fef2f2",
                color: "#DC2626",
                fontSize: 11,
                fontWeight: 600,
              }}
            /> */}
            {/* <Chip
              label={`Renewed: ${renewedCount}`}
              size="small"
              sx={{
                background: "#f0f9ff",
                color: "#0EA5E9",
                fontSize: 11,
                fontWeight: 600,
              }}
            /> */}
          </Box>
        </Box>
      </Box>

      {/* ── Extend Plan Dialog ── */}
      <Dialog
        open={extendOpen}
        onClose={() => !extendSubmitting && setExtendOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            py: 2,
            px: 3,
          }}
        >
          Extend / Change Plan
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}
          >
            {/* Period info banner */}
            <Box
              sx={{
                background: "#eef2f7",
                borderRadius: "8px",
                px: 2,
                py: 1.2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#016551",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: 13, color: "#04343a", fontWeight: 600 }}
              >
                Bulk extending users expiring:&nbsp;
                <span style={{ color: "#016551" }}>
                  {filterPeriods.find((p) => p.value === filterPeriod)?.label}
                </span>
                &nbsp;({userCount} users)
              </Typography>
            </Box>

            {/* Conversion Type */}
            <FormControl size="small" fullWidth>
              <InputLabel>Conversion Type</InputLabel>
              <Select
                label="Conversion Type"
                value={extendForm.conversion}
                onChange={(e) =>
                  setExtendForm((p) => ({
                    ...p,
                    conversion: e.target.value,
                    new_plan_id: "",
                  }))
                }
              >
                <MenuItem value="plan_extension">Plan Extension</MenuItem>
                <MenuItem value="plan_change">Plan Change</MenuItem>
              </Select>
            </FormControl>

            {/* Target Plan */}
            <FormControl size="small" fullWidth>
              <InputLabel>Target Plan</InputLabel>
              <Select
                label="Target Plan"
                value={extendForm.target_plan_id}
                onChange={(e) =>
                  setExtendForm((p) => ({
                    ...p,
                    target_plan_id: e.target.value,
                  }))
                }
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.plan_name || plan.name}
                    {plan.plan_code ? ` — ${plan.plan_code}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* New Plan — only for plan_change */}
            {extendForm.conversion === "plan_change" && (
              <FormControl size="small" fullWidth>
                <InputLabel>New Plan</InputLabel>
                <Select
                  label="New Plan"
                  value={extendForm.new_plan_id}
                  onChange={(e) =>
                    setExtendForm((p) => ({
                      ...p,
                      new_plan_id: e.target.value,
                    }))
                  }
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.plan_name || plan.name}
                      {plan.plan_code ? ` — ${plan.plan_code}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Timedelta - read only, auto from filterPeriod */}
            <TextField
              size="small"
              label="Timedelta (days)"
              value={filterPeriodToTimedelta[filterPeriod] ?? 0}
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiInputBase-input": {
                  color: "#64748b",
                  background: "#f1f5f9",
                },
              }}
            />

            {/* Days Extend */}
            <TextField
              size="small"
              label="Days to Extend"
              type="number"
              value={extendForm.days_extend}
              onChange={(e) =>
                setExtendForm((p) => ({ ...p, days_extend: e.target.value }))
              }
              inputProps={{ min: 1 }}
            />

            {/* Tokens */}
            <TextField
              size="small"
              label="Tokens"
              type="number"
              value={extendForm.tokens}
              onChange={(e) =>
                setExtendForm((p) => ({ ...p, tokens: e.target.value }))
              }
              inputProps={{ min: 0 }}
            />

            {/* Remark */}
            <TextField
              size="small"
              label="Remark"
              multiline
              rows={2}
              value={extendForm.remark}
              onChange={(e) =>
                setExtendForm((p) => ({ ...p, remark: e.target.value }))
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setExtendOpen(false)}
            disabled={extendSubmitting}
            sx={{
              textTransform: "none",
              color: "#64748b",
              borderColor: "#e2e8f0",
              borderRadius: "8px",
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExtendSubmit}
            disabled={extendSubmitting || !extendForm.target_plan_id}
            variant="contained"
            sx={{
              textTransform: "none",
              background: "linear-gradient(135deg,#016551,#014d3d)",
              borderRadius: "8px",
              "&:hover": {
                background: "linear-gradient(135deg,#1db954,#17a349)",
              },
              minWidth: 110,
            }}
          >
            {extendSubmitting ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Reminder Dialog ── */}
      <Dialog
        open={bulkReminderOpen}
        onClose={() => !bulkReminderSubmitting && setBulkReminderOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            py: 2,
            px: 3,
          }}
        >
          Bulk Reminder
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}
          >
            {/* Period info */}
            <Box
              sx={{
                background: "#eef2f7",
                borderRadius: "8px",
                px: 2,
                py: 1.2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#04343a",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: 13, color: "#04343a", fontWeight: 600 }}
              >
                Sending reminders for:&nbsp;
                <span style={{ color: "#016551" }}>
                  {filterPeriods.find((p) => p.value === filterPeriod)?.label}
                </span>
                &nbsp;({userCount} users)
              </Typography>
            </Box>
            {/* Plan selector */}
            <FormControl size="small" fullWidth>
              <InputLabel>Select Plan</InputLabel>
              <Select
                label="Select Plan"
                value={bulkReminderPlanId}
                onChange={(e) => setBulkReminderPlanId(e.target.value)}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.plan_name || plan.name}
                    {plan.plan_code ? ` — ${plan.plan_code}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Timedelta readonly */}
            <TextField
              size="small"
              label="Timedelta (days)"
              value={`${filterPeriods.find((p) => p.value === filterPeriod)?.label} — ${filterPeriodToTimedelta[filterPeriod] ?? 0} day(s)`}
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiInputBase-input": {
                  color: "#64748b",
                  background: "#f1f5f9",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setBulkReminderOpen(false)}
            disabled={bulkReminderSubmitting}
            sx={{ color: "#64748b", borderColor: "#e2e8f0" }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkReminder}
            disabled={bulkReminderSubmitting || !bulkReminderPlanId}
            variant="contained"
            sx={{
              background: "#04343a",
              "&:hover": { background: "#016551" },
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            {bulkReminderSubmitting ? "Sending..." : "Send Reminders"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpiredUserDashboard;
